import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Payment as PaymentIcon,
  Assessment as AssessmentIcon,
  Business as BusinessIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import { useRouter } from "../../../hooks/useRouter";
import { formatCurrency } from "../../../utils";
import FinanceContainer from "../containers/FinanceContainer";
import ImportDialog from "../components/ImportDialog";
import config from "../../../config";

interface Creditor {
  id: string;
  developmentId?: string;
  developmentName?: string;
  fullName: string;
  contactPhone?: string;
  email?: string;
  contactPerson?: string;
  address?: string;
  vatNumber?: string;
  bankName?: string;
  bankAccountNumber?: string;
  paymentTerms?: string;
  totalInvoiced?: number;
  totalPaid?: number;
  balanceDue?: number;
  active?: boolean;
}

const CreditorsList: React.FC = () => {
  const router = useRouter();
  const [creditors, setCreditors] = useState<Creditor[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCreditor, setSelectedCreditor] = useState<Creditor | null>(
    null,
  );
  const [sortField, setSortField] = useState<keyof Creditor>("fullName");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  // Form state for new/edit creditor
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    contactPhone: "",
    contactPerson: "",
    address: "",
    vatNumber: "",
    bankName: "",
    bankAccountNumber: "",
    bankBranchCode: "",
    paymentTerms: "",
    notes: "",
  });

  useEffect(() => {
    fetchCreditors();
  }, []);

  const fetchCreditors = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("vms_access_token");
      const response = await fetch(`${config.API_BASE_URL}/creditors`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setCreditors(data);
      }
    } catch (error) {
      console.error("Error fetching creditors:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: keyof Creditor) => {
    const isAsc = sortField === field && sortDirection === "asc";
    setSortDirection(isAsc ? "desc" : "asc");
    setSortField(field);
  };

  const filteredCreditors = creditors
    .filter(
      (creditor) =>
        creditor.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        creditor.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        creditor.contactPerson
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()),
    )
    .sort((a, b) => {
      const aVal = a[sortField] ?? "";
      const bVal = b[sortField] ?? "";
      if (sortDirection === "asc") {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

  const handleOpenDialog = (creditor?: Creditor) => {
    if (creditor) {
      setSelectedCreditor(creditor);
      setFormData({
        fullName: creditor.fullName || "",
        email: creditor.email || "",
        contactPhone: creditor.contactPhone || "",
        contactPerson: creditor.contactPerson || "",
        address: creditor.address || "",
        vatNumber: creditor.vatNumber || "",
        bankName: creditor.bankName || "",
        bankAccountNumber: creditor.bankAccountNumber || "",
        bankBranchCode: "",
        paymentTerms: creditor.paymentTerms || "",
        notes: "",
      });
    } else {
      setSelectedCreditor(null);
      setFormData({
        fullName: "",
        email: "",
        contactPhone: "",
        contactPerson: "",
        address: "",
        vatNumber: "",
        bankName: "",
        bankAccountNumber: "",
        bankBranchCode: "",
        paymentTerms: "",
        notes: "",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedCreditor(null);
  };

  const handleSaveCreditor = async () => {
    try {
      const token = localStorage.getItem("vms_access_token");
      const url = selectedCreditor
        ? `${config.API_BASE_URL}/creditors/${selectedCreditor.id}`
        : `${config.API_BASE_URL}/creditors`;
      const method = selectedCreditor ? "PATCH" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        fetchCreditors();
        handleCloseDialog();
      }
    } catch (error) {
      console.error("Error saving creditor:", error);
    }
  };

  const handleDeleteCreditor = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this creditor?"))
      return;

    try {
      const token = localStorage.getItem("vms_access_token");
      const response = await fetch(`${config.API_BASE_URL}/creditors/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchCreditors();
      }
    } catch (error) {
      console.error("Error deleting creditor:", error);
    }
  };

  const getBalanceColor = (balance: number | undefined) => {
    if (!balance || balance === 0) return "success";
    if (balance > 0) return "warning";
    return "default";
  };

  const summaryCards = [
    {
      title: "Total Creditors",
      value: creditors.length,
      color: "#1976d2",
    },
    {
      title: "Total Payable",
      value: formatCurrency(
        creditors.reduce((sum, c) => sum + (c.balanceDue || 0), 0),
      ),
      color: "#ff9800",
    },
    {
      title: "Total Invoiced",
      value: formatCurrency(
        creditors.reduce((sum, c) => sum + (c.totalInvoiced || 0), 0),
      ),
      color: "#d32f2f",
    },
    {
      title: "Total Paid",
      value: formatCurrency(
        creditors.reduce((sum, c) => sum + (c.totalPaid || 0), 0),
      ),
      color: "#388e3c",
    },
  ];

  return (
    <FinanceContainer>
      <Box sx={{ p: 2 }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ display: "flex", alignItems: "center", gap: 2 }}
        >
          <BusinessIcon /> Creditors Management
        </Typography>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {summaryCards.map((card, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card sx={{ backgroundColor: card.color, color: "white" }}>
                <CardContent>
                  <Typography variant="subtitle2">{card.title}</Typography>
                  <Typography variant="h5">{card.value}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Actions Bar */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 2,
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <TextField
            placeholder="Search creditors..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
          />
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              onClick={() => setImportDialogOpen(true)}
            >
              Import
            </Button>
            <Button
              variant="outlined"
              startIcon={<AssessmentIcon />}
              onClick={() => router.push("/finance/creditors/age-analysis")}
            >
              Age Analysis
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Creditor
            </Button>
          </Box>
        </Box>

        {/* Creditors Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell>
                  <TableSortLabel
                    active={sortField === "fullName"}
                    direction={sortField === "fullName" ? sortDirection : "asc"}
                    onClick={() => handleSort("fullName")}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>Contact Person</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Payment Terms</TableCell>
                <TableCell align="right">Total Invoiced</TableCell>
                <TableCell align="right">Total Paid</TableCell>
                <TableCell align="right">Balance Due</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredCreditors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No creditors found
                  </TableCell>
                </TableRow>
              ) : (
                filteredCreditors.map((creditor) => (
                  <TableRow key={creditor.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {creditor.fullName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {creditor.email}
                      </Typography>
                    </TableCell>
                    <TableCell>{creditor.contactPerson || "-"}</TableCell>
                    <TableCell>{creditor.contactPhone || "-"}</TableCell>
                    <TableCell>{creditor.paymentTerms || "-"}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(creditor.totalInvoiced || 0)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(creditor.totalPaid || 0)}
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={formatCurrency(creditor.balanceDue || 0)}
                        color={getBalanceColor(creditor.balanceDue)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() =>
                          router.push(`/finance/creditors/${creditor.id}`)
                        }
                        title="View Details"
                      >
                        <ViewIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(creditor)}
                        title="Edit"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() =>
                          router.push(
                            `/finance/creditors/${creditor.id}/payments`,
                          )
                        }
                        title="Payments"
                      >
                        <PaymentIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteCreditor(creditor.id)}
                        title="Delete"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Add/Edit Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {selectedCreditor ? "Edit Creditor" : "Add New Creditor"}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Company/Name"
                  value={formData.fullName}
                  onChange={(e) =>
                    setFormData({ ...formData, fullName: e.target.value })
                  }
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Contact Person"
                  value={formData.contactPerson}
                  onChange={(e) =>
                    setFormData({ ...formData, contactPerson: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Contact Phone"
                  value={formData.contactPhone}
                  onChange={(e) =>
                    setFormData({ ...formData, contactPhone: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={formData.address}
                  onChange={(e) =>
                    setFormData({ ...formData, address: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="VAT Number"
                  value={formData.vatNumber}
                  onChange={(e) =>
                    setFormData({ ...formData, vatNumber: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Payment Terms</InputLabel>
                  <Select
                    value={formData.paymentTerms}
                    label="Payment Terms"
                    onChange={(e) =>
                      setFormData({ ...formData, paymentTerms: e.target.value })
                    }
                  >
                    <MenuItem value="COD">COD</MenuItem>
                    <MenuItem value="Net 7">Net 7</MenuItem>
                    <MenuItem value="Net 14">Net 14</MenuItem>
                    <MenuItem value="Net 30">Net 30</MenuItem>
                    <MenuItem value="Net 60">Net 60</MenuItem>
                    <MenuItem value="Net 90">Net 90</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Bank Name"
                  value={formData.bankName}
                  onChange={(e) =>
                    setFormData({ ...formData, bankName: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Account Number"
                  value={formData.bankAccountNumber}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      bankAccountNumber: e.target.value,
                    })
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Branch Code"
                  value={formData.bankBranchCode}
                  onChange={(e) =>
                    setFormData({ ...formData, bankBranchCode: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveCreditor}>
              {selectedCreditor ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Import Dialog */}
        <ImportDialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          type="creditors"
          onImportComplete={fetchCreditors}
        />
      </Box>
    </FinanceContainer>
  );
};

export default CreditorsList;
